import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Video, AlertTriangle } from 'lucide-react';
import type { LiveAvatarGate } from '../hooks/useLiveAvatar';

interface Props {
  gate: LiveAvatarGate | null;
  onClose: () => void;
}

const LiveAvatarGateModal: React.FC<Props> = ({ gate, onClose }) => {
  const navigate = useNavigate();
  if (!gate) return null;
  // Plan-LiveAvatarLifecycleAndCreditsFallback §B.2.3 — platform-side
  // credit exhaustion is an ops problem, not a customer-actionable one.
  // The status pill in VoicePlayground already communicates the
  // fallback ("AI Avatar temporarily unavailable — using voice-only
  // mode"), so we suppress the modal entirely to avoid confusing the
  // user with an "Upgrade your addon" CTA that wouldn't fix anything.
  if (gate.code === 'platform_credits_exhausted') return null;

  const isNoAddon = gate.code === 'no_addon';

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            {isNoAddon ? (
              <Video className="w-5 h-5 text-indigo-500" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-500" />
            )}
            {isNoAddon
              ? 'AI Avatar not available'
              : gate.code === 'minutes_exhausted'
              ? 'Minutes exhausted'
              : 'All slots busy'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-5 text-sm text-gray-700 dark:text-gray-300">
          <p>{gate.message}</p>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium text-sm"
          >
            Close
          </button>
          {isNoAddon && (
            <button
              onClick={() => {
                onClose();
                navigate('/billing#liveavatar');
              }}
              className="px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold text-sm hover:shadow-lg"
            >
              Go to Billing
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiveAvatarGateModal;
